import { UTM_PARAMETERS_REGEXP } from '../models/constants';
import { FormData, SuccessEvent } from '../models/entities/aem-forms.model';

import UtilsService from './utils.service';

export default class AEMFormsService {
  static AEM_FORMS_SEAMLESS_ID = 'AEMF_FORM_CONTAINER';

  static processCancellationSubmit(
    event: CustomEvent<SuccessEvent[]>,
    aemFormsHandlerConfig: any
  ): any {
    const cancellationVars = aemFormsHandlerConfig.cancellationFieldsMapper;
    return Object.keys(cancellationVars).reduce(
      (response, key) => ({
        ...response,
        [cancellationVars[key]]: event.detail?.[0].formValues[key],
      }),
      {}
    );
  }

  static processFormInitialDataForSeamlessLocalStorageLogic(
    data: any,
    formsValues: { name: string; value: string }[]
  ): any {
    const initialData: any = formsValues;
    const formsKeys = formsValues.map(
      (formValue: { name: any }) => formValue.name
    );

    if (data) {
      Object.keys(data).forEach((property) => {
        if (
          property === 'address' ||
          property === 'companies' ||
          property === 'customerContactsSet' ||
          property === 'customerNameSet'
        ) {
          Object.keys(data[property]).forEach((internalProperty) => {
            if (!formsKeys.includes(internalProperty)) {
              let name = internalProperty;

              if (name === 'houseNo') {
                name = 'houseNumber';
              } else if (name === 'zipCode') {
                name = 'postalCode';
              }

              if (!formsKeys.includes(name)) {
                initialData.push({
                  name,
                  value: data[property][internalProperty],
                });
              }
            }
          });
        } else {
          let name = property;

          if (name === 'lastName1') {
            name = 'lastName';
          } else if (name === 'salutationCode') {
            name = 'salutation';
          } else if (name === 'landlinePhone') {
            name = 'phone';
          }

          if (!formsKeys.includes(name)) {
            initialData.push({
              name,
              value: data[property],
            });
          }
        }
      });
    }

    return initialData;
  }

  static getFormInitialDataFromProspect(prospect): FormData[] {
    const formData: FormData[] = [];
    formData.push(
      {
        name: 'firstName',
        value: prospect.customerNameSet?.firstName,
      },
      {
        name: 'lastName',
        value: prospect.customerNameSet?.lastName,
      },
      {
        name: 'salutation',
        value: prospect.customerNameSet?.salutation,
      },
      {
        name: 'city',
        value: prospect.customerContactsSet?.city,
      },
      {
        name: 'email',
        value: prospect.customerContactsSet?.email,
      },
      {
        name: 'houseNumber',
        value: prospect.customerContactsSet?.houseNumber,
      },
      {
        name: 'phone',
        value: prospect.customerContactsSet?.phone,
      },
      {
        name: 'postalCode',
        value: prospect.customerContactsSet?.postCode,
      },
      {
        name: 'street',
        value: prospect.customerContactsSet?.street,
      },
      {
        name: 'appointmentMessage',
        value: prospect.customerOtherSet?.appointmentMessage,
      },
      {
        name: 'driverLicense',
        value: prospect.customerOtherSet?.drivingLicense,
      }
    );
    return formData;
  }

  static buildProspect(
    aemFormsHandlerConfig: any,
    formValues: any,
    prospect: any
  ): any {
    const computedProspect = AEMFormsService.processProspect(
      aemFormsHandlerConfig,
      formValues,
      prospect
    );

    return computedProspect;
  }

  static processProspect(
    aemFormsHandlerConfig: any,
    formValues: any,
    prospect?: any
  ): any {
    let response = prospect ?? {
      contactOptionsSet: <any>{},
      customerContactsSet: <any>{},
      customerNameSet: <any>{},
      customerOtherSet: <any>{},
      declarationOfConsentSet: <any>{},
    };
    const CONTACT_FORM_PROP_MAPPING = aemFormsHandlerConfig.formStateMapper;
    const consentKeysPrefixes = aemFormsHandlerConfig.checkboxKeys;
    const formValueKeys = Object.keys(formValues);
    formValueKeys.forEach((key) => {
      const consentKeysFound = consentKeysPrefixes.find((prefix: any) =>
        key.startsWith(prefix)
      );
      const finalKey = consentKeysFound ?? key;
      if (CONTACT_FORM_PROP_MAPPING[finalKey]) {
        /**
          privacyOptions and communication options are special keys
          if value is an array it means it is checkbox that can hold multiple values
          also in case of exclusive options radiobuttons or dropdowns can be configured too
          under the same name and needs a custom mapping
        */
        if (consentKeysFound) {
          if (Array.isArray(formValues[key])) {
            // checkboxs
            formValues[key].forEach((opt: string) => {
              response = UtilsService.setNestedValueToObject(
                response,
                `${CONTACT_FORM_PROP_MAPPING[finalKey]}.${opt}`,
                true
              );
            });
          } else {
            // radio buttons or dropdowns
            response = UtilsService.setNestedValueToObject(
              response,
              `${CONTACT_FORM_PROP_MAPPING[finalKey]}.${formValues[key]}`,
              true
            );
          }
        } else {
          /**
            if it is just a single value it can be mapped directly to the map property
          */
          response = UtilsService.setNestedValueToObject(
            response,
            CONTACT_FORM_PROP_MAPPING[finalKey],
            formValues[key]
          );
        }
      }
    });

    if (
      /* eslint-disable camelcase */
      response.customerContactsSet?.pickMeUpAddress_city ||
      response.customerContactsSet?.pickMeUpAddress_houseNumber ||
      response.customerContactsSet?.pickMeUpAddress_postalCode ||
      response.customerContactsSet?.pickMeUpAddress_street
    ) {
      response = UtilsService.setNestedValueToObject(
        response,
        CONTACT_FORM_PROP_MAPPING.pickMeUpActive,
        true
      );
    }

    const utmParameters = UtilsService.getKeyByRegExp(
      formValues,
      UTM_PARAMETERS_REGEXP
    );

    if (utmParameters) {
      response = UtilsService.setNestedValueToObject(
        response,
        'customerOtherSet.utmParameters',
        utmParameters
      );
    }

    return response;
  }
}
